import { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { crmApi, type CrmQuote } from '../../services/api';

/**
 * Upload what came back for a quote, from the quote itself: the signed copy or
 * the Statement of Work. Filed on the quote's opportunity in the contract
 * store, under the client — the same upload the opportunity panel makes.
 *
 * A signed copy accepts an issued quote. With it, a purchase order number is
 * optional: no PO means the invoice says "N/A (signed quote)".
 */
export default function QuoteDocumentUpload({ quote, kind, onClose, onDone }: {
  quote: CrmQuote;
  kind: 'signed_quote' | 'sow';
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [poNumber, setPoNumber] = useState(quote.poNumber || '');
  const [busy, setBusy] = useState(false);
  const signed = kind === 'signed_quote';

  const upload = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    setBusy(true);
    try {
      const res = await crmApi.uploadOpportunityDocument(quote.opportunityId, file, {
        kind, quoteId: quote._id,
        ...(signed && poNumber && poNumber !== (quote.poNumber || '') ? { poNumber } : {}),
      });
      toast.success(res.effects.length ? res.effects.join('; ') : 'Uploaded', { duration: 6000 });
      onDone();
    } catch (err: any) {
      toast.error(err.message || 'Upload failed', { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {signed ? 'Upload the signed quote' : 'Upload the Statement of Work'} — {quote.quoteNumber} v{quote.version}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className={label}>File</label>
            <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              className="text-sm text-gray-600 dark:text-gray-300 file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-gray-100 dark:file:bg-gray-700"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          {signed && (
            <div>
              <label className={label}>Purchase order number</label>
              <input className={field} maxLength={60} value={poNumber}
                placeholder="Leave blank if the signed quote is the order"
                onChange={(e) => setPoNumber(e.target.value)} />
              <p className="mt-1 text-[11px] text-gray-400">
                Blank means the signed quote is the order: invoices show the PO as "N/A (signed quote)".
              </p>
            </div>
          )}
          <p className="text-[11px] text-gray-400">
            {signed
              ? (quote.status === 'issued' ? 'Uploading marks this quote Accepted. ' : '')
                + 'Filed under the client in the contract store.'
              : 'Linked to this quote and filed under the client in the contract store. The quote itself does not change.'}
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">Cancel</button>
          <button onClick={() => void upload()} disabled={busy || !file}
            className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
